import React, { useEffect, useState } from 'react';
import { EmptyState } from '@/components/common/EmptyState';
import { getAcceptedResourcesLocally, getAcceptedResources, JOURNEY_RESOURCE_ACCEPTED_EVENT } from '../../../../hooks/api/psychologist/journeyTracking';
import type { JourneyResource } from '../../../../types/journey';
import { getAllJourneyResources } from './journeyContent';
import { JourneyResourceAccordion } from './JourneyResourceAccordion';

interface Props {
  psychologistId: string;
}

const ALL_RESOURCES: JourneyResource[] = (() => {
  const seen = new Set<string>();
  return getAllJourneyResources().filter((r) => {
    if (seen.has(r.resource_key)) return false;
    seen.add(r.resource_key);
    return true;
  });
})();

/**
 * Columna izquierda de la tab "Camino Inicial": los artículos que la psicóloga ya fue
 * desbloqueando/aceptando, sin importar en qué etapa. Solo lectura — reusa
 * `JourneyResourceAccordion` con `onAccept` como no-op porque estos recursos ya están
 * aceptados (el checkbox queda mostrado como tildado, no interactivo de nuevo).
 */
export const JourneyAcceptedArticlesPanel: React.FC<Props> = ({ psychologistId }) => {
  const [acceptedKeys, setAcceptedKeys] = useState<Set<string>>(() => getAcceptedResourcesLocally(psychologistId));

  useEffect(() => {
    const refresh = () => setAcceptedKeys(getAcceptedResourcesLocally(psychologistId));
    refresh();
    window.addEventListener(JOURNEY_RESOURCE_ACCEPTED_EVENT, refresh);
    return () => window.removeEventListener(JOURNEY_RESOURCE_ACCEPTED_EVENT, refresh);
  }, [psychologistId]);

  // Hidratación server-side (además del refresh por evento de arriba, que solo cubre lo que
  // se acepta EN esta sesión) — trae lo aceptado desde otros dispositivos/sesiones.
  useEffect(() => {
    let cancelled = false;
    getAcceptedResources(psychologistId).then((keys) => { if (!cancelled) setAcceptedKeys(keys); });
    return () => { cancelled = true; };
  }, [psychologistId]);

  const acceptedResources = ALL_RESOURCES.filter((r) => acceptedKeys.has(r.resource_key));

  return (
    <div className="flex flex-col gap-3">
      <div>
        <p className="text-sm font-semibold text-gray-900">Artículos desbloqueados</p>
        <p className="text-xs text-gray-500 mt-0.5">Lo que ya leíste y aceptaste en tu camino inicial.</p>
      </div>
      {acceptedResources.length === 0 ? (
        <EmptyState
          title="Todavía no desbloqueaste artículos"
          description="A medida que aceptes los recursos de la derecha, van a ir apareciendo acá."
          showAction={false}
        />
      ) : (
        <JourneyResourceAccordion
          resources={acceptedResources}
          acceptedKeys={acceptedKeys}
          acceptingKey={null}
          onAccept={() => undefined}
        />
      )}
    </div>
  );
};

export default JourneyAcceptedArticlesPanel;
